import { type ReactNode, use } from 'react'
import cx from 'classnames'
import {
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
  DisclosureStateContext,
  Heading as AriaHeading,
  GridListItem,
  GridListContext,
  GridList,
  type GridListProps,
  type Key,
} from 'react-aria-components'
import { usePress } from 'react-aria'
import { RawButton } from '@/components/Button'
import {
  CardItemContentDecoration,
  CardItemIcon,
  getSkeletonClassName,
} from '@/components/Card'
import { useIsSkeleton, type SkeletonColor } from '@/components/Skeleton'
import flattenChildren from 'react-keyed-flatten-children'
import styles from './ExpandableCardList.module.css'
import type { Ref } from 'react'

interface ExpandableCardListProps<T extends object = object> {
  children?: GridListProps<T>['children']
  spacingY?: 'sm' | 'lg'
  'aria-label'?: string
  'aria-labelledby'?: string
  renderEmptyState?: () => ReactNode
}

export function ExpandableCardList<T extends object = object>({
  children,
  spacingY = 'sm',
  renderEmptyState,
  ...rest
}: ExpandableCardListProps<T>) {
  return (
    <GridListContext value={{ selectionMode: undefined }}>
      <GridList<T>
        {...rest}
        selectionMode={undefined}
        className={cx(styles.list, spacingY === 'lg' && styles.spacingYLg)}
        renderEmptyState={
          renderEmptyState ? () => renderEmptyState() : undefined
        }
      >
        {children}
      </GridList>
    </GridListContext>
  )
}

interface ExpandableCardListItemProps {
  children?: ReactNode
  isExpanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (isExpanded: boolean) => void
  textValue?: string
  id?: Key
  ref?: Ref<HTMLDivElement>
  isDisabled?: boolean
  isSkeleton?: boolean
  skeletonColor?: SkeletonColor
  testId?: string
}

export function ExpandableCardListItem({
  children,
  isExpanded,
  defaultExpanded,
  onExpandedChange,
  textValue,
  id,
  ref,
  isDisabled,
  isSkeleton,
  skeletonColor,
  testId,
}: ExpandableCardListItemProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <GridListItem
      id={id}
      ref={ref}
      isDisabled={isDisabled || isSkeleton}
      textValue={textValue}
      data-test={testId}
      className={cx(
        styles.item,
        getSkeletonClassName(isSkeleton, skeletonColor),
      )}
    >
      <AriaDisclosure
        isExpanded={isExpanded}
        defaultExpanded={defaultExpanded}
        onExpandedChange={onExpandedChange}
        data-test={testId}
        isDisabled={isDisabled || isSkeleton}
      >
        {children}
      </AriaDisclosure>
    </GridListItem>
  )
}

interface ExpandableCardListItemContentProps {
  children?: ReactNode
  extras?: ReactNode
  icon?: ReactNode | null | false
  decoration?: ReactNode | null | false
  layout?: 'default' | 'stacked'
  spacingY?: 'lg' | 'base'
  spacingRight?: 'lg' | 'sm'
  isSkeleton?: boolean
  align?: 'top' | 'center'
  headingLevel?: 2 | 3 | 4 | 5 | 6
  testId?: string
}

export function ExpandableCardListItemContent({
  children,
  extras,
  icon,
  isSkeleton,
  spacingY = 'lg',
  spacingRight = 'lg',
  align = 'top',
  decoration,
  layout,
  headingLevel = 3,
  testId,
}: ExpandableCardListItemContentProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { isExpanded, toggle } = use(DisclosureStateContext) ?? {}

  const { pressProps } = usePress({
    onPress: toggle,
  })

  return (
    <AriaHeading
      level={headingLevel}
      {...pressProps}
      className={cx(
        styles.itemContent,
        align === 'center' && styles.alignCenter,
        spacingY === 'base' && styles.spacingBase,
        spacingRight === 'sm' && styles.spacingRightSm,
        layout === 'stacked' && styles.stacked,
      )}
    >
      {decoration
        ? decoration
        : icon && <CardItemContentDecoration>{icon}</CardItemContentDecoration>}

      <div className={styles.itemContentText}>{children}</div>
      <div className={styles.itemContentExtrasContainer}>
        {flattenChildren(extras).length > 0 && <div>{extras}</div>}

        <RawButton
          slot="trigger"
          testId={testId}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <div className={cx(styles.arrowContainer)}>
            <CardItemIcon
              skeleton={isSkeleton}
              variant="secondary"
              name="chevron"
              rotate={isExpanded ? 90 : 0}
            />
          </div>
        </RawButton>
      </div>
    </AriaHeading>
  )
}

interface ExpandableCardListItemPanelProps {
  children?: ReactNode
}

export function ExpandableCardListItemPanel({
  children,
}: ExpandableCardListItemPanelProps) {
  return <AriaDisclosurePanel>{children}</AriaDisclosurePanel>
}

interface ExpandableCardListEmptyStateProps {
  title: ReactNode
  action?: ReactNode
  testId?: string
}

export function ExpandableCardListEmptyState({
  title,
  action,
  testId,
}: ExpandableCardListEmptyStateProps) {
  return (
    <div data-test={testId} className={styles.cardListEmptyState}>
      <p className={styles.cardListEmptyStateTitle}>{title}</p>
      {action && (
        <div className={styles.cardListEmptyStateAction}>{action}</div>
      )}
    </div>
  )
}
